import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Search, Building2, ChevronRight, Circle, Filter, Archive, Edit2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const statusColors = {
  active: 'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/20',
  inactive: 'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/20',
  archived: 'text-[#52525b] bg-white/5 border-white/10',
  provisioning: 'text-[#3B82F6] bg-[#3B82F6]/10 border-[#3B82F6]/20',
};
const planBadge = { starter: 'text-[#a1a1aa] border-white/10', pro: 'text-[#3B82F6] border-[#3B82F6]/30', enterprise: 'text-[#EF694C] border-[#EF694C]/30' };

const ChannelDot = ({ status }) => (
  <Circle className={`w-2 h-2 fill-current ${status === 'active' ? 'text-[#10B981]' : 'text-[#52525b]'}`} />
);

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', plan: 'starter', owner_email: '', owner_name: '' });

  const fetchTenants = () => {
    api.get('/superadmin/tenants')
      .then(r => setTenants(r.data))
      .catch(() => toast.error('Failed to load tenants'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTenants(); }, []);

  const filtered = tenants.filter(t => {
    const matchSearch = t.name.toLowerCase().includes(search.toLowerCase()) || t.slug.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug || !form.owner_email || !form.owner_name) {
      toast.error('Fill all fields'); return;
    }
    setCreating(true);
    try {
      await api.post('/superadmin/tenants', form);
      toast.success('Mandant wurde erstellt');
      setShowCreate(false);
      setForm({ name: '', slug: '', plan: 'starter', owner_email: '', owner_name: '' });
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create tenant');
    } finally { setCreating(false); }
  };

  const handleArchive = async (id, name) => {
    if (!window.confirm(`Archive ${name}? This will disable the tenant.`)) return;
    try {
      await api.delete(`/superadmin/tenants/${id}`);
      toast.success('Mandant archiviert');
      fetchTenants();
    } catch { toast.error('Failed to archive'); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h2 className="font-heading font-bold text-2xl text-white">Tenants</h2>
          <p className="text-[#a1a1aa] text-sm mt-1">{tenants.length} total tenants</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="create-tenant-btn"
          className="flex items-center gap-2 h-10 px-4 bg-[#EF694C] hover:bg-[#d65d43] text-white text-sm font-semibold rounded-lg shadow-[0_0_20px_rgba(239,105,76,0.3)] hover:shadow-[0_0_30px_rgba(239,105,76,0.5)] transition-all duration-200 active:scale-95">
          <Plus className="w-4 h-4" /> Neuer Mandant
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 animate-fade-in-up stagger-1">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525b]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Mandanten suchen..."
            data-testid="tenant-search-input"
            className="w-full h-10 bg-[#121214] border border-white/10 rounded-lg pl-9 pr-4 text-white text-sm placeholder-[#52525b] focus:outline-none focus:border-[#EF694C] transition-all" />
        </div>
        <div className="flex gap-2">
          {['all', 'active', 'inactive', 'archived'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}
              className={`px-3 h-10 text-sm rounded-lg border transition-all capitalize ${statusFilter === s ? 'bg-[#EF694C]/10 text-[#EF694C] border-[#EF694C]/20' : 'text-[#a1a1aa] border-white/10 hover:bg-white/5'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden animate-fade-in-up stagger-2">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#EF694C]/30 border-t-[#EF694C] rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-12 h-12 text-[#52525b] mx-auto mb-3" />
            <p className="text-[#a1a1aa]">No tenants found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                {['Mandant', 'Tarif', 'Status', 'Kanäle', 'Aktionen'].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#52525b]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map(t => (
                <tr key={t.id} className="hover:bg-white/[0.03] transition-colors group" data-testid={`tenant-row-${t.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#EF694C]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-[#EF694C]">{t.name.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{t.name}</p>
                        <p className="text-xs text-[#52525b] font-mono">{t.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs font-semibold uppercase tracking-wider ${planBadge[t.plan]?.split(' ')[0] || 'text-[#a1a1aa]'}`}>
                      {t.plan}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-md border font-medium ${statusColors[t.status] || 'text-[#a1a1aa] bg-white/5 border-white/10'}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5">
                      {['email', 'whatsapp', 'instagram', 'facebook', 'widget'].map(c => (
                        <div key={c} title={`${c}: ${t.channel_status?.[c] || 'unknown'}`}>
                          <ChannelDot status={t.channel_status?.[c]} />
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Link to={`/superadmin/tenants/${t.id}`} data-testid={`view-tenant-${t.id}`}
                        className="p-1.5 text-[#a1a1aa] hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                        <Edit2 className="w-4 h-4" />
                      </Link>
                      {t.status !== 'archived' && (
                        <button onClick={() => handleArchive(t.id, t.name)} data-testid={`archive-tenant-${t.id}`}
                          className="p-1.5 text-[#a1a1aa] hover:text-[#F43F5E] hover:bg-[#F43F5E]/10 rounded-lg transition-colors">
                          <Archive className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Mandant erstellen Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md" data-testid="create-tenant-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl">Create Neuer Mandant</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            {[
              { label: 'Company Name', key: 'name', placeholder: 'Acme Corporation' },
              { label: 'Slug (subdomain)', key: 'slug', placeholder: 'acme' },
              { label: 'Admin-E-Mail', key: 'owner_email', placeholder: 'admin@kunde.de', type: 'email' },
              { label: 'Admin-Name', key: 'owner_name', placeholder: 'John Doe' },
            ].map(f => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-[#a1a1aa] mb-1.5">{f.label}</label>
                <input type={f.type || 'text'} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder={f.placeholder} data-testid={`create-tenant-${f.key}`}
                  className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm placeholder-[#52525b] focus:outline-none focus:border-[#EF694C] transition-all" />
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium text-[#a1a1aa] mb-1.5">Plan</label>
              <select value={form.plan} onChange={e => setForm(p => ({ ...p, plan: e.target.value }))}
                data-testid="create-tenant-plan"
                className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all">
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <p className="text-xs text-[#52525b]">Default password for the owner account: <span className="font-mono text-[#a1a1aa]">changeme123</span></p>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowCreate(false)} data-testid="cancel-create-tenant"
                className="flex-1 h-10 border border-white/10 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded-lg text-sm font-medium transition-all">
                Cancel
              </button>
              <button type="submit" disabled={creating} data-testid="confirm-create-tenant"
                className="flex-1 h-10 bg-[#EF694C] hover:bg-[#d65d43] text-white font-semibold rounded-lg text-sm shadow-[0_0_20px_rgba(239,105,76,0.3)] transition-all disabled:opacity-60">
                {creating ? 'Wird erstellt...' : 'Mandant erstellen'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
